import React, { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuthV2 } from '../../contexts/AuthContextV2';
import AuthForm from '../AuthForm';
import LoadingScreen from '../common/LoadingScreen';
import Dashboard from '../Dashboard/Dashboard';
import { AdminToolsPage } from '../AdminTools/AdminToolsPage';
import { UserProfilePage } from '../../pages/UserProfilePage';
import { GuidePage } from '../../pages/GuidePage';
import { WebsiteAnalyticsPage } from '../../pages/WebsiteAnalyticsPage';
import { AgentsPage } from '../../pages/AgentsPage';
import { ManufacturingPage } from '../../pages/ManufacturingPage';
import { ManufacturingPartPage } from '../../pages/ManufacturingPartPage';
import { DownloadsPage } from '../../pages/DownloadsPage';
import { AcceptInvitationPage } from '../../pages/AcceptInvitationPage';
import { ForgotPasswordPage } from '../../pages/ForgotPasswordPage';
import { ResetPasswordPage } from '../../pages/ResetPasswordPage';
import { PendingApprovalPage } from '../../pages/PendingApprovalPage';
import { trackPageView } from '../../utils/analytics';
import { parseFolderRoute } from '../file-browser/folderUrl';

interface AuthGuardProps {
  children?: React.ReactNode;
}

export const AuthGuard: React.FC<AuthGuardProps> = () => {
  const { isAuthenticated, isLoading } = useAuthV2();
  const location = useLocation();

  // Dashboard owns its own page_views (per-tab) since tab changes go through
  // pushState rather than react-router. Skip every url the Dashboard owns to
  // avoid double-counting — that is `/` and, since B-5, the `/files...` family.
  useEffect(() => {
    if (location.pathname === '/') return;
    if (parseFolderRoute(location.pathname).kind !== 'none') return;
    trackPageView(location.pathname);
  }, [location.pathname]);

  // Public routes that don't require authentication
  const publicRoutes = [
    '/accept-invitation',
    '/forgot-password',
    '/reset-password',
    '/pending-approval',
  ];
  const isPublicRoute = publicRoutes.some((route) => location.pathname.startsWith(route));

  // If on a public route, render it without authentication check
  if (isPublicRoute) {
    return (
      <Routes>
        <Route path="/accept-invitation" element={<AcceptInvitationPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/pending-approval" element={<PendingApprovalPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    );
  }

  // Show loading screen while checking authentication
  if (isLoading) {
    return <LoadingScreen />;
  }

  // Show login form if not authenticated
  if (!isAuthenticated) {
    return <AuthForm />;
  }

  // User is authenticated and has organization access - show the app with routing
  return (
    <Routes>
      {/* One `<Dashboard>` instance for `/` and every Files url (STD-029 B-5).
          A NESTED layout route, not four siblings rendering the same component:
          react-router keeps a PARENT route's element mounted while only the
          child match changes, so `/` ⇄ `/files/folder/x` ⇄ `/files/folder/y`
          cannot remount the tab, re-run `initializeFromUrl`, or re-fire the GA
          page_view. Siblings would rely on React reconciling by element type,
          which is an implementation detail rather than a contract. The children
          render nothing; the parent needs no `<Outlet/>`.

          ⚠ EVERY url the store can write must have a route here. Ranking in v6
          is by specificity so `path="*"` cannot swallow `/files/...` — but a url
          with no route at all lands on it and the deep link is silently erased.

          ⚠ Do NOT read `useParams()` / `useLocation()` inside the Dashboard
          subtree. Dashboard urls are written with raw `pushState` by
          `stores/dashboardUrlSync.ts`, which react-router does not observe, so
          its location is stale between navigations. The folder id comes from
          `parseFolderRoute(window.location.pathname)` via the store. */}
      <Route path="/" element={<Dashboard />}>
        <Route index element={null} />
        <Route path="files" element={null} />
        {/* ONE folder route, and it is a SPLAT. `parseFolderRoute`'s contract is
            "everything after the id is ignored"; the `:slug` route this replaced
            made that true of exactly-four-segment urls only, so a hand-typed
            `/files/folder/<id>/foo/bar` matched nothing, fell through to `*` and
            was erased before the parser ever saw it.

            The splat matches an EMPTY tail, so it covers `/files/folder/<id>`
            too — measured, not assumed: with the standalone `:objectId` route
            deleted the whole browser spec still passed, which is what makes that
            route dead weight rather than defence in depth. It is deleted so the
            mutation ratchet keeps biting: with two overlapping routes, removing
            either one broke nothing and "every url needs a route" had no proof
            left. */}
        <Route path="files/folder/:objectId/*" element={null} />
      </Route>
      <Route path="/admin-tools" element={<AdminToolsPage />} />
      <Route path="/guide" element={<GuidePage />} />
      <Route path="/website-analytics" element={<WebsiteAnalyticsPage />} />
      <Route path="/agents" element={<AgentsPage />} />
      <Route path="/manufacturing" element={<ManufacturingPage />} />
      <Route path="/manufacturing/part/:partId" element={<ManufacturingPartPage />} />
      <Route path="/downloads" element={<DownloadsPage />} />
      <Route path="/user/:userId" element={<UserProfilePage />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

export default AuthGuard;
